import { Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../components/confirm-dialog/confirm-dialog.component';
import { MapFeatureLink } from '../models/map-feature-link';
import { SheetMarkupQueueService } from '../services/sheet-markup-queue.service';

/**
 * W77: removing a record's link (its drawing location or linked feature) loses markup when strokes are held for it (an
 * unsaved link), the record has saved markup on the sheet, or its map capture or snapshot is marked up. A bare pin loses nothing:
 * the record is not saved until Done / Save.
 */
export function linkRemovalLosesMarkup(link: MapFeatureLink | null | undefined, heldStrokes = false): boolean {
  return !!link && (heldStrokes || !!link.sheet?.has_markup || !!link.capture?.has_markup || !!link.snapshot?.has_markup);
}

/**
 * Rule 5 of a markup-only location (no pin): the markup editor asks this before saving with every stroke removed,
 * since the drawing location goes with the markup (confirmLinkRemoval's wording).
 */
export const PINLESS_EMPTY_CONFIRM = {
  title: 'Remove drawing location?',
  message: 'This record has no pin on the sheet, so with no markup it has no drawing location.',
  confirm: 'Remove',
};

/** The sheet viewer's editor on a markup-only record: emptying it there would leave a location with nothing to show */
export const PINLESS_EMPTY_HERE = 'This record has no pin, so its markup is its location. To remove it, open the record.';

/** The app's confirm, only when markup would be lost; otherwise true at once. Emits whether to go ahead. */
export function confirmLinkRemoval(dialog: MatDialog, link: MapFeatureLink | null | undefined, heldStrokes = false): Observable<boolean> {
  if (!linkRemovalLosesMarkup(link, heldStrokes)) {
    return of(true);
  }
  const model = link.sheet
    ? new ConfirmDialogModel('Remove drawing location?', 'Your markup for it on this sheet is removed too.', 'Remove', 'Cancel', true)
    : new ConfirmDialogModel('Remove linked feature?', 'Your markup on its map capture is removed too.', 'Remove', 'Cancel', true);
  return dialog.open(ConfirmDialogComponent, { disableClose: true, data: model }).afterClosed().pipe(map(confirmed => !!confirmed));
}

/**
 * Whether the picker holds strokes for this link (an unsaved sheet link). Looked up lazily through `injector`, so a host
 * whose tests do not provide the queue's dependencies is unaffected; false when the queue is unavailable.
 */
export function linkHeldStrokes(injector: Injector, link: MapFeatureLink | null | undefined): boolean {
  const sheet = link?.sheet;
  if (!sheet?.revision_uuid || link.uuid) {
    return false;
  }
  try {
    return !!injector.get(SheetMarkupQueueService).heldFor(link);
  } catch {
    return false;
  }
}

/**
 * A Clear or Remove that drops `link`: runs `drop` at once when no markup would go, else after the confirm says Remove
 * (held strokes looked up through `injector`). One call for hosts whose clear is a plain method.
 */
export function dropLinkWithConfirm(dialog: MatDialog, injector: Injector, link: MapFeatureLink | null | undefined, drop: () => void): void {
  confirmLinkRemoval(dialog, link, linkHeldStrokes(injector, link)).subscribe(go => {
    if (go) drop();
  });
}
